import React, { useEffect, useState } from 'react';
import styled from '@emotion/native';
import { useRouter, Href } from 'expo-router';
import StylistProfile from '@/shared/components/StylistProfile';
import StylistPreviewHeader from '@/shared/components/navigation/StylistPreviewHeader';
import { FullScreenLoader } from '@/shared/components/FullScreenLoader';
import { BodyText } from '@/shared/components/StyledText';
import { green, sand } from '@/shared/styles/colors';
import SentryService from '@/services/sentry/Sentry.service';
import DownloadAppButton from '@/components/stylist-profile/DownloadAppButton';
import { useAuth } from '@/services/auth/AuthProvider';
import useStore from '@/stores/useStore';
import { ROUTES } from '@/shared/constants/routes';
import { ActivityIndicator } from 'react-native';
import { webEnvironment } from '@/shared/config';
import { EventType, PricingTier, Gender } from '@/shared/types/generated';
import useIsDesktop from '@/shared/hooks/useIsDesktop';
import { IsDesktopProps } from '@/shared/styles/types';

interface StylistUser {
  firstName: string;
  lastName: string;
  gender?: Gender | null;
  birthDate?: Date | null;
  profilePicUrl?: string | null;
}

interface StylistPricing {
  id: number;
  hourlyRate: number;
  tierName: PricingTier;
}

interface StylistData {
  id: number;
  expertise: string[];
  aesthetics: string[];
  instagram?: string | null;
  tiktok?: string | null;
  substack?: string | null;
  bio: string;
  landscapeImage?: string | null;
  portraitImages: string[];
  user: StylistUser;
  pricing?: StylistPricing | null;
  customHourlyRate?: number | null;
  hasCompletedStripeSetup: boolean;
  isEventAttendee: boolean;
  isFavorited: boolean;
  customInPersonHourlyRate?: number | null;
  computedInPersonAvailability: boolean;
}

interface NextAvailableSlot {
  startTime: string;
  eventType: EventType;
  formattedDate: string;
}

export default function StylistPreview({ shareCode }: { shareCode: string }) {
  const router = useRouter();
  const { isAuthenticated, loadingAuth } = useAuth();
  const isStylist = useStore((state) => state.isStylist);
  const [stylistId, setStylistId] = useState<number | null>(null);
  const [stylistData, setStylistData] = useState<StylistData | null>(null);
  const [nextAvailableSlot, setNextAvailableSlot] =
    useState<NextAvailableSlot | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingStylistId, setLoadingStylistId] = useState(false);
  const [loadingStylist, setLoadingStylist] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const isDesktop = useIsDesktop();

  // Redirect authenticated clients to explore page
  useEffect(() => {
    if (loadingAuth) return;

    if (isAuthenticated && !isStylist && shareCode) {
      setIsRedirecting(true);
      // Defer navigation to next tick to ensure root layout is mounted
      setTimeout(() => {
        router.replace(
          `${ROUTES.EXPLORE.path}?s=${encodeURIComponent(shareCode)}` as Href,
        );
      }, 800);
    }
  }, [isAuthenticated, isStylist, loadingAuth, shareCode, router]);

  // Fetch stylist ID by share code
  useEffect(() => {
    async function fetchStylistId() {
      if (!shareCode || loadingAuth || (isAuthenticated && !isStylist)) {
        return;
      }

      setLoadingStylistId(true);
      try {
        const response = await fetch(
          `${webEnvironment.server.baseUrl}/api/stylist/by-share-code/${shareCode}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error ||
              'Unable to load stylist profile. Please try again later.',
          );
        }

        const data = await response.json();
        setStylistId(data.stylistId);
      } catch (error) {
        SentryService.captureError(
          'Error fetching stylist by share code',
          error,
        );
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Unable to load stylist profile. Please try again later.',
        );
      } finally {
        setLoadingStylistId(false);
      }
    }

    fetchStylistId();
  }, [shareCode, isAuthenticated, isStylist, loadingAuth]);

  // Fetch stylist data and next available slot
  useEffect(() => {
    async function fetchStylistData() {
      if (!stylistId) {
        return;
      }

      setLoadingStylist(true);
      try {
        // Fetch stylist data
        const stylistResponse = await fetch(
          `${webEnvironment.server.baseUrl}/api/stylist/${stylistId}`,
          {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
          },
        );

        if (!stylistResponse.ok) {
          const errorData = await stylistResponse.json();
          throw new Error(
            errorData.error ||
              'Unable to load stylist profile. Please try again later.',
          );
        }

        const stylist = await stylistResponse.json();
        setStylistData(stylist);

        // Fetch next available slot
        try {
          const slotResponse = await fetch(
            `${webEnvironment.server.baseUrl}/api/stylist/${stylistId}/next-available-slot?currentDate=${new Date().toISOString()}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            },
          );

          if (slotResponse.ok) {
            const slot = await slotResponse.json();
            setNextAvailableSlot(slot);
          }
        } catch (slotError) {
          // Non-critical error - just log it
          SentryService.captureError(
            `Error fetching next available slot for stylist ${stylistId}`,
            slotError,
          );
        }
      } catch (error) {
        SentryService.captureError('Error fetching stylist profile', error);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : 'Unable to load stylist profile. Please try again later.',
        );
      } finally {
        setLoadingStylist(false);
      }
    }

    fetchStylistData();
  }, [stylistId]);

  // Show loading while auth is loading or while redirecting
  if (loadingAuth || isRedirecting) {
    return (
      <Container>
        <StylistPreviewHeader />
        <LoadingContainer>
          <ActivityIndicator color={green} size="large" />
        </LoadingContainer>
      </Container>
    );
  }

  if (loadingStylistId || loadingStylist) {
    return (
      <Container>
        <StylistPreviewHeader />
        <FullScreenLoader />
      </Container>
    );
  }

  if (errorMessage) {
    return (
      <Container>
        <StylistPreviewHeader />
        <ErrorContainer>
          <ErrorText>{errorMessage}</ErrorText>
        </ErrorContainer>
      </Container>
    );
  }

  if (!stylistData) {
    return (
      <Container>
        <StylistPreviewHeader />
        <ErrorContainer>
          <ErrorText>Stylist not found</ErrorText>
        </ErrorContainer>
      </Container>
    );
  }

  const stylistName =
    `${stylistData.user.firstName} ${stylistData.user.lastName.charAt(0)}.`.toUpperCase();

  return (
    <Container>
      <StylistPreviewHeader />
      <ContentWrapper>
        <StylistProfile
          stylistName={stylistName}
          expertise={stylistData.expertise}
          aesthetics={stylistData.aesthetics}
          profilePicUrl={stylistData.user?.profilePicUrl || ''}
          instagramHandle={stylistData.instagram}
          tiktokHandle={stylistData.tiktok}
          substackHandle={stylistData.substack}
          bio={stylistData.bio}
          landscapeImage={stylistData.landscapeImage || null}
          portraitImages={stylistData.portraitImages}
          pricing={stylistData.pricing || undefined}
          customHourlyRate={stylistData.customHourlyRate || undefined}
          customInPersonHourlyRate={
            stylistData.customInPersonHourlyRate || undefined
          }
          isAvailableInPerson={stylistData.computedInPersonAvailability}
          nextAvailableSlot={nextAvailableSlot}
          showConsultationButton={false}
          showSubheaderSection={false}
          showPreviewLabel={false}
          isModalView={false}
          isPreviewView
        />
      </ContentWrapper>
      <DownloadSection isDesktop={isDesktop}>
        <DownloadText isDesktop={isDesktop}>
          Bookings are available through the app
        </DownloadText>
        <DownloadAppButton />
      </DownloadSection>
    </Container>
  );
}

const Container = styled.View({
  flex: 1,
  backgroundColor: sand,
});

const ContentWrapper = styled.View({
  flex: 1,
});

const LoadingContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
});

const ErrorContainer = styled.View({
  flex: 1,
  justifyContent: 'center',
  alignItems: 'center',
  padding: 20,
});

const ErrorText = styled(BodyText)({
  fontSize: 16,
  color: green,
  textAlign: 'center',
});

const DownloadSection = styled.View<IsDesktopProps>(({ isDesktop }) => ({
  width: '100%',
  backgroundColor: sand,
  paddingHorizontal: 30,
  paddingTop: 16,
  paddingBottom: 16,
  alignItems: 'center',
  gap: isDesktop ? 19 : 12,
  height: isDesktop ? 86 : 141,
  flexDirection: isDesktop ? 'row' : 'column',
  justifyContent: 'center',
}));

const DownloadText = styled(BodyText)<IsDesktopProps>(({ isDesktop }) => ({
  fontSize: isDesktop ? 20 : 17,
  fontWeight: '600',
  color: green,
  textAlign: 'center',
}));
